import { CartAddEvent } from '@theme/events';

// Section-scoped, progressively enhanced and safe for Theme Editor reloads.
class CustomCollectionPage extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', (event) => this.selectVariant(event), { signal });
    this.addEventListener('submit', (event) => this.submit(event), { signal });
    document.addEventListener('click', (event) => {
      if (!this.querySelector('.lcc__filters')?.contains(event.target)) this.closeFilters();
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeFilters(true);
    }, { signal });
  }

  disconnectedCallback() { this.controller?.abort(); }

  closeFilters(restoreFocus = false) {
    const details = this.querySelector('.lcc__filters[open]');
    if (!details) return;
    details.open = false;
    if (restoreFocus) details.querySelector('summary')?.focus();
  }

  selectVariant(event) {
    const choice = event.target.closest('[data-variant-choice]');
    if (!choice || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const card = choice.closest('.lcc-card');
    if (card.querySelector('[aria-busy=true]')) return;
    event.preventDefault();
    card.querySelectorAll('[data-variant-choice]').forEach((swatch) => swatch.removeAttribute('aria-current'));
    choice.setAttribute('aria-current', 'true');
    card.querySelectorAll('[data-product-link]').forEach((link) => { link.href = choice.href; });
    const input = card.querySelector('[name=id]');
    if (input) input.value = choice.dataset.variantId;
    const quantity = card.querySelector('[name=quantity]');
    if (quantity) quantity.value = choice.dataset.quantity;
    card.querySelector('[data-price]').textContent = choice.dataset.price;
    const image = card.querySelector('.lcc-card__image');
    if (image && choice.dataset.image) {
      image.removeAttribute('srcset');
      image.src = choice.dataset.image;
      image.alt = choice.dataset.imageAlt;
    }
    const available = choice.dataset.available === 'true';
    const button = card.querySelector('button.lcc-card__bag');
    if (button) {
      button.disabled = !available;
      button.setAttribute('aria-label', `${available ? card.dataset.addLabel : card.dataset.soldLabel} — ${card.querySelector('h2').textContent} — ${choice.dataset.title}`);
    }
    card.querySelector('[data-status]').textContent = available ? '' : card.dataset.soldLabel;
  }

  async submit(event) {
    const form = event.target;
    if (!form.matches('.lcc-card__form')) return;
    event.preventDefault();
    const button = form.querySelector('button');
    if (button.disabled) return;
    const card = form.closest('.lcc-card');
    const status = card.querySelector('[data-status]');
    const body = new FormData(form);
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
    if (sectionIds.length) body.set('sections', sectionIds.join(','));
    body.set('sections_url', location.pathname);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    status.textContent = '';
    try {
      const response = await fetch(`${form.action.replace(/\/$/, '')}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || card.dataset.errorLabel);
      // Reuse the theme's public cart event so its drawer and count stay in sync.
      this.dispatchEvent(new CartAddEvent({}, form.id, {
        source: 'custom-collection-page', productId: card.dataset.productId,
        variantId: String(body.get('id')), itemCount: Number(body.get('quantity')),
        sections: result.sections,
      }));
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && result.sections && Object.values(result.sections).some(Boolean)) {
        drawer.open?.();
      } else {
        // Full cart page is the reliable fallback if section rendering is unavailable.
        location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      }
    } catch (error) {
      status.textContent = error.message || card.dataset.errorLabel;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('custom-collection-page')) customElements.define('custom-collection-page', CustomCollectionPage);
